import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useMeusEquipamentosQuery } from '@/features/SI/hooks/data';
import { SI_PORTAL_BASE_PATH } from '../../siPortal.definitions';
import { useMinhasSolicitacoes } from '../SiSolicitacoesPage/useMinhasSolicitacoes';
import { useMeusEstadosQuery, usePublicacoesPublicadasQuery } from '../../publicacoes/usePublicacoes';
import { maisLidas, noFeed, pendenciasDeCiencia, textoPrazo } from '../../publicacoes/publicacao.definitions';
import type { Publicacao } from '../../publicacoes/publicacao.types';
import { FeedPublicacoes } from '../../publicacoes/components/FeedPublicacoes';
import { PendenciasCienciaFaixa } from '../../publicacoes/components/PendenciasCienciaFaixa';

/** Post abre na Início; documento abre na aba Documentos (onde fica o "Li e estou ciente"). */
const linkDaPublicacao = (publicacao: Publicacao) =>
  publicacao.tipo === 'documento'
    ? `${SI_PORTAL_BASE_PATH}/documentos/${publicacao.id}`
    : `${SI_PORTAL_BASE_PATH}/inicio/publicacao/${publicacao.id}`;

const MOSTRAR_MAIS_LIDAS = false;

const CARTAO_LISTA = 'divide-y divide-raro-gray-100 overflow-hidden rounded-2xl border border-raro-gray-200 bg-white shadow-sm';

interface AtalhoProps {
  titulo: string;
  to: string;
  resumo: string;
  descricao: string;
}

function Atalho({ titulo, to, resumo, descricao }: AtalhoProps) {
  return (
    <section className="space-y-3">
      <h2 className="font-montserrat text-base font-semibold text-raro-gray-900">{titulo}</h2>
      <Link
        to={to}
        className="flex items-center justify-between gap-3 rounded-2xl border border-raro-gray-200 bg-white px-5 py-4 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
      >
        <div>
          <p className="text-sm font-medium text-raro-gray-900">{resumo}</p>
          <p className="text-xs text-raro-gray-500">{descricao}</p>
        </div>
        <ArrowRightIcon className="h-4 w-4 shrink-0 text-primary-600" aria-hidden="true" />
      </Link>
    </section>
  );
}

function resumoEquipamentos(comVoce: number, aCaminho: number): string {
  if (comVoce === 0 && aCaminho === 0) return 'Nenhum no seu nome';
  const partes = [comVoce > 0 ? `${comVoce} com você` : null, aCaminho > 0 ? `${aCaminho} a caminho` : null];
  return partes.filter(Boolean).join(' · ');
}

/**
 * Aba "Início" (Fase 3 do Portal do SI): faixa de ciências pendentes, feed de informativos
 * (fixadas primeiro, 6 por vez) e, na lateral, "Aguardando sua ciência", "Mais lidas" (desde a
 * 1ª publicação) e atalhos pras solicitações e equipamentos da pessoa.
 */
export default function SiInicioPage() {
  const { user } = useAuth();
  const agora = useMemo(() => Date.now(), []);
  const publicadas = usePublicacoesPublicadasQuery();
  const lista = useMemo(() => publicadas.data ?? [], [publicadas.data]);
  // Feed: posts + documentos anunciados. Pendências e "Mais lidas" contam tudo (inclusive documentos).
  const doFeed = useMemo(() => lista.filter(noFeed), [lista]);
  const { estados } = useMeusEstadosQuery(lista);
  const minhas = useMinhasSolicitacoes();
  const meusEquipamentos = useMeusEquipamentosQuery();

  const pendencias = useMemo(
    () => pendenciasDeCiencia(lista, estados, user?.collaboratorId, agora),
    [lista, estados, user?.collaboratorId, agora]
  );
  // "Mais lidas" oculta por enquanto (decisão: atalhos de solicitações/equipamentos são mais
  // importantes na lateral). Pra voltar: MOSTRAR_MAIS_LIDAS = true.
  const topLidas = useMemo(() => (MOSTRAR_MAIS_LIDAS ? maisLidas(lista) : []), [lista]);
  const emAberto = minhas.data.filter((s) => s.status === 'aberta' || s.status === 'em_andamento').length;
  const equipamentos = meusEquipamentos.data ?? [];
  const aCaminho = equipamentos.filter((e) => e.transitoSentido === 'envio').length;

  return (
    <div className="space-y-6">
      <div data-tour="si-faixa-ciencia">
        <PendenciasCienciaFaixa pendencias={pendencias} linkDe={linkDaPublicacao} />
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2" data-tour="si-feed">
          <FeedPublicacoes
            publicacoes={doFeed}
            estados={estados}
            colaboradorId={user?.collaboratorId}
            agora={agora}
            isLoading={publicadas.isLoading}
            linkDe={linkDaPublicacao}
          />
        </div>

        <aside className="space-y-6">
          {pendencias.length > 0 && (
            <section className="space-y-3" data-tour="si-aguardando">
              <h2 className="font-montserrat text-base font-semibold text-raro-gray-900">
                Aguardando sua ciência <span className="text-xs font-normal text-raro-gray-500">({pendencias.length})</span>
              </h2>
              <ul className={CARTAO_LISTA}>
                {pendencias.map(({ publicacao, situacao }) => (
                  <li key={publicacao.id}>
                    <Link
                      to={linkDaPublicacao(publicacao)}
                      className="flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-primary-50/60"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-raro-gray-900">{publicacao.titulo}</p>
                        <p
                          className={`text-xs ${
                            situacao.tipo === 'atrasada'
                              ? 'font-semibold text-rose-600'
                              : situacao.tipo === 'vence'
                                ? 'text-amber-700'
                                : 'text-raro-gray-500'
                          }`}
                        >
                          {textoPrazo(situacao)}
                        </p>
                      </div>
                      <ChevronRightIcon className="h-4 w-4 shrink-0 text-raro-gray-400" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {topLidas.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-montserrat text-base font-semibold text-raro-gray-900">Mais lidas</h2>
              <ul className={`${CARTAO_LISTA} text-sm`}>
                {topLidas.map((publicacao) => (
                  <li key={publicacao.id}>
                    <Link
                      to={linkDaPublicacao(publicacao)}
                      className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-primary-50/60"
                    >
                      <span className="min-w-0 truncate text-raro-gray-800">{publicacao.titulo}</span>
                      <span className="shrink-0 text-raro-gray-400">{publicacao.leiturasTotal}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="space-y-6" data-tour="si-atalhos">
          <Atalho
            titulo="Suas solicitações"
            to={`${SI_PORTAL_BASE_PATH}/solicitacoes`}
            resumo={minhas.isLoading ? 'Carregando...' : emAberto === 0 ? 'Nenhuma em aberto' : `${emAberto} em aberto`}
            descricao="Abrir uma nova ou acompanhar as suas"
          />
          <Atalho
            titulo="Seus equipamentos"
            to={`${SI_PORTAL_BASE_PATH}/solicitacoes?lista=equipamentos`}
            resumo={
              meusEquipamentos.isLoading
                ? 'Carregando...'
                : meusEquipamentos.isError
                  ? 'Ver seus equipamentos'
                  : resumoEquipamentos(equipamentos.length - aCaminho, aCaminho)
            }
            descricao="O que está com você, termos, devolução e troca"
          />
          </div>
        </aside>
      </div>
    </div>
  );
}
